import * as keytar from '@napi-rs/keyring/keytar.js';
import { z } from 'zod';
import * as fs from 'fs/promises';
import * as path from 'path';
import config from '../../config';

function getSecretFilePath(service: string, account: string): string {
  return path.join(config.configDir, 'secrets', `${service}-${account}.json`);
}

async function storeSecretsToFile(
  service: string,
  account: string,
  data: unknown
): Promise<void> {
  const filePath = getSecretFilePath(service, account);
  await fs.mkdir(path.dirname(filePath), { recursive: true, mode: 0o700 });
  await fs.writeFile(filePath, JSON.stringify(data), { mode: 0o600 });
}

async function loadSecretsFromFile(
  service: string,
  account: string
): Promise<string | null> {
  const filePath = getSecretFilePath(service, account);
  try {
    return await fs.readFile(filePath, 'utf-8');
  } catch {
    return null;
  }
}

export async function storeSecrets({
  service,
  account,
  data,
}: {
  service: string;
  account: string;
  data: unknown;
}): Promise<void> {
  try {
    await keytar.setPassword(service, account, JSON.stringify(data));
  } catch (err) {
    console.error(err);
    console.warn(
      '\nFailed to store secrets in keyring, falling back to file storage.'
    );
    await storeSecretsToFile(service, account, data);
  }
}

export async function loadSecrets<T>({
  service,
  account,
  schema,
}: {
  service: string;
  account: string;
  schema: z.ZodSchema<T>;
}): Promise<T | null> {
  const data =
    (await keytar.getPassword(service, account)) ??
    (await loadSecretsFromFile(service, account));
  if (!data) return null;
  const parsed = schema.safeParse(JSON.parse(data));
  if (!parsed.success) {
    console.warn('Invalid data from secure storage');
    return null;
  }
  return parsed.data;
}
